import { Cloud, CloudOff, FileInput, Link2Off } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/primitives";
import {
  Dialog,
  DialogContent,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  Tip,
} from "@/components/ui/overlays";
import { keys, useDriveStatus } from "@/hooks/queries";
import { trackJob } from "@/hooks/use-upload";
import { api, queryClient, type DocumentOut } from "@/lib/api";
import { useActivity } from "@/stores/activity";

/** Drive is an ADDON: the vault on this device is primary (ADR-0004).
 * The chip stays quiet unless the user reaches for it. */
export function DriveChip() {
  const status = useDriveStatus();
  const [importOpen, setImportOpen] = useState(false);
  const [fileId, setFileId] = useState("");
  const connected = status.data?.connected === true;

  const connect = async () => {
    try {
      const { auth_url } = await api.request<{ auth_url: string }>("GET", "/api/auth/google/login");
      window.open(auth_url, "_blank", "width=520,height=640");
      toast("Finish linking in the Google window…");
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const s = await api.request<{ connected: boolean }>("GET", "/api/auth/google/status");
        if (s.connected) {
          toast.success("Drive linked (drive.file scope)");
          await queryClient.invalidateQueries({ queryKey: keys.drive });
          return;
        }
      }
    } catch (err) {
      toast.error("Drive is not configured on the backend", {
        description: err instanceof Error ? err.message.slice(0, 120) : String(err),
      });
    }
  };

  const importFile = async () => {
    const id = fileId.trim();
    if (!id) return;
    setImportOpen(false);
    setFileId("");
    try {
      const res = await api.request<{ document: DocumentOut; job_id: string | null }>(
        "POST",
        "/api/documents/drive",
        { file_id: id, title: null },
      );
      if (!res.job_id) {
        toast.info(`${res.document.title} is already in the atlas`);
        return;
      }
      useActivity.getState().upsert({
        id: res.job_id,
        kind: "ingest",
        title: res.document.title,
        docId: res.document.id,
        status: "queued",
      });
      const job = await trackJob(res.job_id, res.document.id);
      if (job.status === "done") {
        toast.success(`${res.document.title} imported from Drive`);
        await queryClient.invalidateQueries({ queryKey: keys.overview });
      } else {
        toast.error("Drive import failed", { description: job.error ?? "" });
      }
    } catch (err) {
      toast.error("Drive import failed", {
        description: err instanceof Error ? err.message.slice(0, 140) : String(err),
      });
    }
  };

  return (
    <>
      <Dropdown>
        <Tip label="Google Drive (optional sync)">
          <DropdownTrigger asChild>
            <button
              className="flex items-center gap-1.5 rounded-md border border-border px-2 py-1 font-mono text-2xs text-muted-foreground transition-colors hover:border-border hover:text-muted-foreground"
              aria-label="Google Drive (optional sync)"
            >
              {connected ? <Cloud className="h-3.5 w-3.5 text-viridian" /> : <CloudOff className="h-3.5 w-3.5" />}
              drive
            </button>
          </DropdownTrigger>
        </Tip>
        <DropdownContent align="end">
          {connected ? (
            <>
              <DropdownItem onSelect={() => setImportOpen(true)}>
                <FileInput className="h-3.5 w-3.5 text-muted-foreground" /> Import by file id…
              </DropdownItem>
              <DropdownItem
                onSelect={() =>
                  void api.request("DELETE", "/api/auth/google").then(() => {
                    toast("Drive unlinked");
                    void queryClient.invalidateQueries({ queryKey: keys.drive });
                  })
                }
              >
                <Link2Off className="h-3.5 w-3.5 text-muted-foreground" /> Unlink account
              </DropdownItem>
            </>
          ) : (
            <DropdownItem onSelect={() => void connect()}>
              <Cloud className="h-3.5 w-3.5 text-muted-foreground" /> Connect Google Drive
            </DropdownItem>
          )}
        </DropdownContent>
      </Dropdown>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent title="Import from Drive">
          <p className="mb-3 text-xs leading-relaxed text-muted-foreground">
            Paste a Drive file id (from the file’s URL). It is fetched once with the{" "}
            <span className="font-mono text-primary">drive.file</span> scope, filed into your local
            vault, and charted.
          </p>
          <div className="flex gap-2">
            <Input
              value={fileId}
              onChange={(e) => setFileId(e.target.value)}
              placeholder="1AbC…xyz"
              autoFocus
            />
            <Button variant="default" onClick={() => void importFile()} disabled={!fileId.trim()}>
              Import
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
